import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/student/PageHeader";
import { Colors, useLayoutCommon } from "@/contexts/LayoutContext";
import logo_icon from "@/assets/images/logos/ofcourse-bar-logo.svg";
import { Button } from "@/components/ui/button";
import { Award, BookOpen, CheckCircle, Loader2, PartyPopper } from "lucide-react";
import { isPoolBasedProgression } from "@/domain/moduleProgressionMode";
import { useModuleCompletionScreen } from "@/hooks/student/useModuleCompletion";
import { getModuleInfo } from "@/services/student/module.service";
import type { ModuleInfo } from "@/services/student/module.interfaces";

/**
 * Tela de transição após concluir todos os exercícios de conteúdo do módulo.
 * Modo somente-exercícios: celebra aprovação e convida a seguir para o próximo módulo.
 * Modo com teste de módulo: informa que é necessário passar no teste para desbloquear o próximo.
 */
export const ModuleCompletionPage = () => {
  const { setBackgroundColor } = useLayoutCommon();
  const { moduleId } = useParams<{ moduleId: string }>();
  const navigate = useNavigate();
  const [moduleInfo, setModuleInfo] = useState<ModuleInfo | null>(null);

  const {
    completionStatus,
    isLoading,
    error,
    isApproved,
    scorePercentage,
    totalExercises,
    correctAnswers,
    progressionMode,
    nextModuleId,
    isNextModuleAvailable,
    handleContinue,
    handleRetryContentExercises,
    isRetryingContentExercises,
  } = useModuleCompletionScreen(moduleId ?? null);

  useEffect(() => {
    setBackgroundColor(Colors.WHITE);
  }, [setBackgroundColor]);

  useEffect(() => {
    if (!moduleId) return;
    let cancelled = false;
    getModuleInfo(moduleId)
      .then((info) => {
        if (!cancelled) setModuleInfo(info);
      })
      .catch(() => {
        if (!cancelled) setModuleInfo(null);
      });
    return () => {
      cancelled = true;
    };
  }, [moduleId]);

  const subtitle =
    moduleInfo?.name ??
    moduleInfo?.title ??
    (moduleId ? `Módulo` : "Módulo");

  const isModuleTestPath = isPoolBasedProgression(progressionMode);
  const showTestCta =
    isModuleTestPath &&
    Boolean(completionStatus?.isCompleted) &&
    !isNextModuleAvailable;

  const onContinue = () => {
    handleContinue();
  };

  const goToModuleTest = () => {
    if (!moduleId) return;
    navigate(`/student/modules/${moduleId}/test`);
  };

  const goToDashboard = () => {
    navigate("/student/dashboard");
  };

  if (!moduleId) {
    return (
      <div className="max-w-[1227px] w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <p className="text-center text-gray-600">Módulo não encontrado.</p>
        <div className="flex justify-center mt-4">
          <Button type="button" variant="outline" onClick={goToDashboard}>
            Voltar ao painel
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="max-w-[1227px] w-full mx-auto px-4 sm:px-6 lg:px-8 py-16 flex flex-col items-center gap-4">
        <Loader2 className="w-10 h-10 animate-spin text-primary" />
        <p className="text-gray-600">Carregando seu progresso…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-[1227px] w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="w-full bg-red-50 rounded-xl p-6 text-center text-red-700">
          Não foi possível carregar o status de conclusão. Tente novamente.
        </div>
        <div className="flex justify-center mt-4 gap-2">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            Voltar
          </Button>
          <Button type="button" onClick={goToDashboard}>
            Painel
          </Button>
        </div>
      </div>
    );
  }

  if (completionStatus && !completionStatus.isCompleted) {
    return (
      <div className="max-w-[1227px] w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader
          title="Conclusão do módulo"
          subtitle={subtitle}
          avatar={logo_icon}
          avatarAlt="Logo OfCourse"
          showActions={true}
        />
        <div className="w-full bg-amber-50 rounded-xl p-6 mt-6 text-center text-amber-900">
          <p className="font-medium">
            Você ainda não concluiu todos os exercícios deste módulo.
          </p>
          <Button
            type="button"
            className="mt-6 min-h-[44px]"
            onClick={() => navigate(`/student/exercises/${moduleId}`)}
          >
            <BookOpen className="w-4 h-4 mr-2" />
            Ir para os exercícios
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1227px] w-full mx-auto px-4 sm:px-6 lg:px-8">
      <PageHeader
        title="Módulo concluído"
        subtitle={subtitle}
        avatar={logo_icon}
        avatarAlt="Logo OfCourse"
        showActions={true}
      />

      <div className="w-full bg-gray-50 rounded-xl p-6 sm:p-8 mt-6 flex flex-col items-center gap-6 text-center">
        {isApproved ? (
          <>
            <PartyPopper className="w-16 h-16 text-amber-500" />
            <p className="text-2xl font-semibold text-gray-800">
              Parabéns! Conteúdo do módulo concluído.
            </p>
          </>
        ) : (
          <>
            <Award className="w-16 h-16 text-amber-600" />
            <p className="text-2xl font-semibold text-gray-800">
              Módulo concluído — meta de aprovação
            </p>
            <p className="text-gray-600 max-w-lg">
              Você respondeu a todos os exercícios, mas a nota ficou abaixo de 90%.
              Tente novamente — apenas as respostas erradas serão liberadas para refazer.
            </p>
          </>
        )}

        <div className="flex flex-wrap justify-center gap-4 text-gray-700">
          <span className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 text-sm border border-gray-200">
            <CheckCircle className="w-4 h-4 text-green-600" />
            Acertos: {completionStatus?.correctAnswers ?? correctAnswers} /{" "}
            {completionStatus?.answeredExercises ??
              totalExercises}
          </span>
          <span className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 text-sm border border-gray-200">
            Nota: {scorePercentage.toFixed(0)}%
          </span>
        </div>

        {isModuleTestPath && (
          <div className="max-w-xl text-gray-700 text-left bg-white rounded-lg border border-gray-200 p-4">
            <p className="font-medium text-gray-900">Próximo passo: teste do módulo</p>
            <p className="text-sm mt-2">
              Neste módulo há teste formal. Passar no teste é o que desbloqueia o
              próximo módulo na sequência do pacote.
            </p>
          </div>
        )}

        {!isModuleTestPath &&
          isApproved &&
          isNextModuleAvailable &&
          nextModuleId && (
          <p className="text-gray-600 max-w-lg">
            O próximo módulo já está liberado. Você pode continuar o estudo quando quiser.
          </p>
        )}

        {!isModuleTestPath && isApproved && !nextModuleId && (
          <p className="text-gray-600 max-w-lg">
            Parabéns pelo progresso
          </p>
        )}

        <div className="flex flex-col sm:flex-row gap-3 w-full max-w-md justify-center">
          {showTestCta && (
            <Button
              type="button"
              className="min-h-[44px]"
              onClick={() => void goToModuleTest()}
            >
              Fazer teste do módulo
            </Button>
          )}

          {!isApproved && (
            <Button
              type="button"
              className="min-h-[44px]"
              disabled={isRetryingContentExercises}
              onClick={handleRetryContentExercises}
            >
              {isRetryingContentExercises ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Preparando…
                </>
              ) : (
                "Tentar novamente"
              )}
            </Button>
          )}

          {isApproved && (!isModuleTestPath || isNextModuleAvailable) && (
            <Button
              type="button"
              className="min-h-[44px]"
              onClick={onContinue}
            >
              Continuar
            </Button>
          )}

          <Button
            type="button"
            variant="outline"
            className="min-h-[44px]"
            onClick={goToDashboard}
          >
            Painel
          </Button>
        </div>
      </div>
    </div>
  );
};
